import { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { showAlert } from '@/slice/AlertSlice';
import {
  SEND_FAILURE_COPY,
  SEND_TOAST_VARIANTS,
  type SendCopy,
  type SendOutcome
} from './sendOutcome';
import { sendTeamEmail } from './sendTeamEmail';
import { useTeamEmail, type TeamEmail } from './recipients/useTeamEmail';
import {
  MAX_TEAM_RECIPIENTS,
  useTeamRecipients,
  type TeamRecipients
} from './recipients/useTeamRecipients';
import { useShareWithTeamMutation } from './services/shareApi';

export const TEAM_SEND_COPY: SendCopy = {
  sentLine: (who) =>
    `Sent to ${who}. They’ll get an email from PlanOmatic with a link to this Photoset.`,
  sentToast: (who) => `Photoset shared with ${who}`,
  // From the modal, a 400 means the list it holds is not the one asset-service
  // allows (a member left the team since it loaded): the list is read again.
  invalid: 'Your team list changed. Check who’s selected and send again.',
  throttled: 'You’ve sent a lot of share emails. Try again later.',
  unavailable: SEND_FAILURE_COPY.unavailable
};

/**
 * Why Send is disabled with a line saying so:
 *  - `hidden`: the photoset is hidden from the client, so the link would open
 *    on an unavailable page. Read from the page's visibility state, never from
 *    a 409, which has other causes.
 *  - `unavailable`: this environment cannot send the team email, or could not
 *    say whether it can.
 *  - `too-many`: more members checked than one send takes.
 */
export type InternalShareBlock = 'hidden' | 'unavailable' | 'too-many';

export const INTERNAL_SHARE_BLOCK_COPY: Record<InternalShareBlock, string> = {
  hidden: 'Make the page visible to the client before sending it to your team.',
  unavailable: 'Emailing your team isn’t available right now.',
  'too-many': `Send to ${MAX_TEAM_RECIPIENTS} people or fewer at a time.`
};

export interface InternalShare {
  team: TeamRecipients;
  note: string;
  setNote: (note: string) => void;
  sending: boolean;
  block: InternalShareBlock | null;
  canSend: boolean;
  /** The last send's shortfall or failure, or null. A send that reaches
   *  everyone calls `onSent` instead. */
  outcome: SendOutcome | null;
  send: () => void;
}

interface UseInternalShareArgs {
  photosetId: string;
  /** From the page's visibility state (`GalleryLinkGate.hiddenFromClient`). */
  hiddenFromClient: boolean;
  /**
   * Every email went: the Share modal closes, with the toast as the
   * confirmation, as the Sync modal's webhook send does. The design keeps it
   * open on an 8-second "Sent to …" line; closing was decided in DV-3715's
   * QA (2026-10-29).
   * A shortfall or a failure keeps it open, with those certainly not sent
   * still checked.
   */
  onSent: () => void;
}

/**
 * The Internal Share tab's state and send (DV-3715): the team list, who is
 * checked (nobody to start with), the note, and what the last send came to.
 * Held by the Share modal rather than the tab, so a look at the Photoset
 * Gallery tab does not clear the form.
 */
export const useInternalShare = ({
  photosetId,
  hiddenFromClient,
  onSent
}: UseInternalShareArgs): InternalShare => {
  const dispatch = useDispatch();
  const team = useTeamRecipients(photosetId);
  const teamEmail = useTeamEmail();
  const [shareWithTeam, { isLoading: sending }] = useShareWithTeamMutation();
  const [note, setNote] = useState('');
  const [outcome, setOutcome] = useState<SendOutcome | null>(null);
  // Set synchronously, so a second click before the re-render cannot send twice.
  const inFlight = useRef(false);

  const block = sendBlock(hiddenFromClient, teamEmail, team.picked.length);
  const canSend =
    block === null &&
    teamEmail === 'on' &&
    team.list.status === 'ready' &&
    !team.refreshing &&
    team.picked.length > 0 &&
    !sending;

  const send = async () => {
    if (!canSend || inFlight.current) return;
    inFlight.current = true;
    setOutcome(null);
    const next = await sendTeamEmail({
      shareWithTeam,
      photosetId,
      picked: team.picked,
      note,
      copy: TEAM_SEND_COPY
    });
    inFlight.current = false;
    if (next.failure === 'invalid') team.retry();
    dispatch(showAlert({ type: SEND_TOAST_VARIANTS[next.tone], message: next.toast }));
    if (next.allSent) {
      onSent();
      return;
    }
    setOutcome(next);
    team.pick(next.keepSelected);
  };

  return {
    team,
    note,
    setNote,
    sending,
    block,
    canSend,
    outcome,
    send: () => void send()
  };
};

/** The one line Send's disabled state gets, most actionable first. */
const sendBlock = (
  hiddenFromClient: boolean,
  teamEmail: TeamEmail,
  pickedCount: number
): InternalShareBlock | null => {
  if (hiddenFromClient) return 'hidden';
  if (teamEmail === 'off') return 'unavailable';
  if (pickedCount > MAX_TEAM_RECIPIENTS) return 'too-many';
  return null;
};
